/**
 * ActiveFilterChips Component
 *
 * Displays active filters as removable chips/badges.
 */

import { X } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Badge } from '../../../../ui/badge';
import type { ActiveFilterChipsProps, ActiveFilterChip, FilterConfig, FilterLogic } from '../types/filter.types';
import {
  DIMENSION_LABELS,
  formatDateRangeLabel,
} from '../constants/filter.constants';

/**
 * Get logic suffix for display in filter chips.
 * Always shows the logic mode for filters that support toggling (dimensions, regulatory flags).
 */
function getLogicSuffix(logic: FilterLogic | undefined): string {
  if (!logic) return '';
  return ` (${logic === 'and' ? 'all' : 'any'})`;
}

/**
 * Build list of active filter chips from config.
 */
function buildChips(
  filters: FilterConfig,
  onRemove: ActiveFilterChipsProps['onRemoveFilter']
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  // Date range - for custom range, only show chip when both dates are set
  const dateRange = filters.dateRange;
  const isPresetOtherThanCustom = dateRange?.preset && dateRange.preset !== 'custom';
  const isCustomWithBothDates = dateRange?.preset === 'custom' && dateRange?.from && dateRange?.to;

  if (isPresetOtherThanCustom || isCustomWithBothDates) {
    const dateLabel = formatDateRangeLabel(
      dateRange.preset,
      dateRange.from,
      dateRange.to
    );

    chips.push({
      id: 'dateRange',
      type: 'dateRange',
      label: `Date: ${dateLabel}`,
      onRemove: () => onRemove('dateRange'),
    });
  }

  // Dimensions - always show logic mode (any/all)
  const dimensionLogicSuffix = getLogicSuffix(filters.dimensions?.logic);
  filters.dimensions?.values?.forEach((dimension) => {
    chips.push({
      id: `dimension-${dimension}`,
      type: 'dimension',
      label: `Dimension: ${DIMENSION_LABELS[dimension]}${dimensionLogicSuffix}`,
      onRemove: () => onRemove('dimension', dimension),
    });
  });

  // Search
  if (filters.search) {
    chips.push({
      id: 'search',
      type: 'search',
      label: `Search: "${filters.search}"`,
      onRemove: () => onRemove('search'),
    });
  }

  // Conduct score range
  if (filters.conductScoreMin !== undefined || filters.conductScoreMax !== undefined) {
    const min = filters.conductScoreMin ?? 1;
    const max = filters.conductScoreMax ?? 10;
    chips.push({
      id: 'conductScore',
      type: 'conductScore',
      label: `Score: ${min}-${max}`,
      onRemove: () => onRemove('conductScore'),
    });
  }

  return chips;
}

export function ActiveFilterChips({
  filters,
  onRemoveFilter,
  className,
}: ActiveFilterChipsProps) {
  const chips = buildChips(filters, onRemoveFilter);

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)} data-testid="active-filter-chips">
      {chips.map((chip) => (
        <Badge
          key={chip.id}
          variant="outline"
          className="flex items-center gap-1 rounded-full bg-card pr-1.5 text-muted-foreground"
        >
          <span>{chip.label}</span>
          <button
            type="button"
            onClick={chip.onRemove}
            className="ml-1 rounded-full p-0.5 hover:bg-muted-foreground/20"
            aria-label={`Remove ${chip.label} filter`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}
